import { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
  Grid,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import { AuthContext } from "../../../shared/context/auth-context";
import { formatarDataGuardada } from "../../../shared/util/datasGuardadas";
import { useLoading } from "../../../shared/context/LoadingContext";
import NotaInformativa from "../../../shared/components/notaInformativa/NotaInformativa";

const MOTIVOS_DESCARTE = [
  { value: "EXPIRADO", label: "Produto expirado/vencido" },
  { value: "DANIFICADO", label: "Produto danificado" },
  { value: "CONTAMINADO", label: "Produto contaminado" },
  { value: "RECOLHIDO_FABRICANTE", label: "Recolhido pelo fabricante" },
  { value: "ENTRADA_INCORRETA", label: "Entrada incorreta no sistema" },
  { value: "PERDIDO", label: "Produto perdido/extraviado" },
  { value: "QUEBRADO", label: "Produto quebrado" },
  { value: "OUTRO", label: "Outro motivo" },
];

// "ARM01 / PRAT-A — Prateleira A"
const descreverLocalizacao = (entrada) => {
  const loc = entrada?.localizacao;
  const armazem = loc?.armazem?.codigo || "N/A";
  const nome = loc?.nome ? ` — ${loc.nome}` : "";
  return `${armazem} / ${loc?.codigo || "N/A"}${nome}`;
};

const localizacoesComStock = (loteInfo) =>
  (loteInfo?.localizacoes || []).filter((l) => l.quantidade > 0);

const ModalDescarteStock = ({
  open,
  handleClose,
  dados,
  sendRequest,
  stockId,
  atualizarStock,
}) => {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);

  const locs = localizacoesComStock(dados?.loteInfo);
  const temLocalizacoes = locs.length > 0;
  const varias = locs.length > 1;

  const totalDisponivel = temLocalizacoes
    ? locs.reduce((s, l) => s + l.quantidade, 0)
    : dados?.loteInfo?.quantidade || 0;

  /* Um lote a zero não tem nada para descartar, e no entanto continua a ocupar
     uma linha na lista. É o caso de quem lançou o lote errado: a quantidade já
     foi corrigida por um descarte e ficou a carcaça. Aqui o modal deixa de
     pedir quantidades e passa a eliminar a linha. */
  const eliminarLote = totalDisponivel === 0;

  // O modal é renderizado condicionalmente pelo Stock, por isso remonta a cada
  // abertura — o estado inicial pode vir direto do lote, sem efeito a repô-lo.

  // "lote" descarta tudo o que existe; "localizacao" deixa afinar prateleira a
  // prateleira. Só faz sentido escolher quando há mais do que uma.
  const [modo, setModo] = useState("lote");

  // Quantidade por localização, indexada pelo id da localização. Guardadas como
  // string para o campo poder ficar vazio enquanto se escreve.
  const [quantidades, setQuantidades] = useState(() =>
    locs.reduce((acc, l) => {
      acc[l.localizacao._id] = String(l.quantidade);
      return acc;
    }, {}),
  );
  // Lotes sem localizações nenhumas continuam a ter um único campo.
  const [quantidadeSimples, setQuantidadeSimples] = useState(() =>
    String(dados?.loteInfo?.quantidade || 0),
  );

  /* A eliminar, o motivo é o único que existe — fica escolhido à partida e o
     campo trancado, para não haver a hipótese de eliminar um lote a dizer que
     estava expirado. */
  const [formData, setFormData] = useState({
    motivo: eliminarLote ? "ENTRADA_INCORRETA" : "",
    observacoes: "",
  });
  const [errors, setErrors] = useState({});

  const disponivelEm = (locId) =>
    locs.find((l) => l.localizacao._id === locId)?.quantidade || 0;

  const totalADescartar = temLocalizacoes
    ? locs.reduce((s, l) => s + (Number(quantidades[l.localizacao._id]) || 0), 0)
    : Number(quantidadeSimples) || 0;

  // O `max` de um <input type="number"> só trava as setas — escrever à mão ou
  // colar passa à mesma. Limitamos aqui para que o campo nunca chegue a conter
  // mais do que existe.
  const limitar = (valor, maximo, anterior) => {
    if (valor === "") return "";
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return anterior;
    if (numero > maximo) return String(maximo);
    if (numero < 0) return "0";
    return valor;
  };

  const setQuantidadeLoc = (locId, valor) => {
    setQuantidades((prev) => ({
      ...prev,
      [locId]: limitar(valor, disponivelEm(locId), prev[locId]),
    }));
    if (errors.quantidade) setErrors((p) => ({ ...p, quantidade: null }));
  };

  // Voltar ao modo "lote inteiro" repõe as quantidades cheias — é o que o modo
  // significa, e evita submeter valores meio-editados do outro modo.
  const trocarModo = (novoModo) => {
    if (!novoModo) return; // clique no botão já ativo
    setModo(novoModo);
    if (novoModo === "lote") {
      setQuantidades(
        locs.reduce((acc, l) => {
          acc[l.localizacao._id] = String(l.quantidade);
          return acc;
        }, {}),
      );
    }
    setErrors((p) => ({ ...p, quantidade: null }));
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validarForm = () => {
    const novosErros = {};

    if (!formData.motivo) {
      novosErros.motivo = "Selecione um motivo para o descarte";
    }

    /* A eliminar não há quantidade nenhuma para validar — mas exige-se a
       observação. Apagar uma linha do inventário sem dizer porquê deixa quem
       vier a seguir sem maneira de perceber o que aconteceu. */
    if (eliminarLote) {
      if (!formData.observacoes) {
        novosErros.observacoes =
          "Diga porque é que este lote foi lançado por engano";
      }
    } else if (!Number.isFinite(totalADescartar) || totalADescartar <= 0) {
      novosErros.quantidade = "Indique a quantidade a descartar";
    } else if (totalADescartar > totalDisponivel) {
      novosErros.quantidade = `A quantidade não pode exceder ${totalDisponivel}`;
    }

    if (formData.motivo === "OUTRO" && !formData.observacoes) {
      novosErros.observacoes =
        "Para 'Outro motivo', é necessário fornecer observações";
    }

    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = async () => {
    if (!validarForm()) return;

    startLoading();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("produtoId", dados.produtoInfo._id);
      formDataToSend.append("loteId", dados.loteInfo._id);
      formDataToSend.append("motivo", formData.motivo);
      formDataToSend.append("observacoes", formData.observacoes || "");
      formDataToSend.append("autorizadoPor", auth.userId);

      if (!eliminarLote) {
        if (temLocalizacoes) {
          // O backend aceita a repartição e cria um movimento de saída por
          // localização, para o histórico ficar fiel.
          const linhas = locs
            .map((l) => ({
              localizacaoId: l.localizacao._id,
              quantidade: Number(quantidades[l.localizacao._id]) || 0,
            }))
            .filter((l) => l.quantidade > 0);
          formDataToSend.append("localizacoes", JSON.stringify(linhas));
        } else {
          formDataToSend.append("quantidade", quantidadeSimples);
        }
      }

      /* Endpoints diferentes porque são operações diferentes: um dá baixa de
         unidades e cria descarte e movimentos; o outro só remove uma linha
         vazia. Passá-las pela mesma função obrigava a atravessar a transação do
         descarte com quantidade zero. */
      const rota = eliminarLote
        ? `eliminarLoteStock/${stockId}`
        : `registrarDescarteStock/${stockId}`;

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/${rota}`,
        "POST",
        formDataToSend,
        { Authorization: "Bearer " + auth.token }
      );

      if (response && response.stock) {
        atualizarStock(
          response.stock,
          eliminarLote
            ? response.message || "Lote eliminado."
            : "Descarte registrado com sucesso!",
        );
        handleClose();
      }
    } catch (err) {
      // O sendRequest já mostra a mensagem devolvida pelo backend (ex.: a
      // quantidade excede a disponível). Um toast genérico aqui só a taparia.
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const unidade = dados?.produtoInfo?.unidadeMedida || "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "#ffebee",
          color: "#d32f2f",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <DeleteIcon color="error" />
        {eliminarLote ? "Eliminar Lote do Stock" : "Registrar Descarte de Stock"}
      </DialogTitle>

      <DialogContent dividers>
        {/* No caso de eliminar a linha já há uma caixa própria a explicá-lo mais
            abaixo — duas explicações seguidas sobre coisas diferentes fazem com
            que não se leia nenhuma. */}
        {!eliminarLote && (
          <NotaInformativa
            titulo="O que é um descarte"
            style={{ margin: "0 0 20px" }}
          >
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              <li>
                Descarte é dar baixa de unidades que{" "}
                <strong>já não podem ser usadas</strong> — expiraram,
                partiram-se, foram contaminadas ou perderam-se. Não é o mesmo
                que <strong>retirada</strong>, que é o consumo normal do
                produto.
              </li>
              <li>
                As unidades saem do inventário e ficam registadas como perda,
                com o motivo, o responsável e a data. É por aqui que depois se
                percebe quanto se perde e porquê.
              </li>
              <li>
                Se o lote foi <strong>lançado com a quantidade errada</strong>,
                escolha <em>Entrada incorreta no sistema</em> — assim a correção
                não fica contada como desperdício.
              </li>
            </ul>
          </NotaInformativa>
        )}
        {dados && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informações do Produto
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Produto:</strong> {dados.produtoInfo?.nome}
                </Typography>
                <Typography variant="body1">
                  <strong>Categoria:</strong>{" "}
                  {dados.produtoInfo?.categoriaStock?.nome || "N/A"}
                </Typography>
                <Typography variant="body1">
                  <strong>Unidade:</strong> {unidade || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Lote:</strong> {dados.loteInfo?.lote}
                </Typography>
                <Typography variant="body1">
                  <strong>Quantidade disponível:</strong> {totalDisponivel}
                </Typography>
                {dados.loteInfo?.dataValidade && (
                  <Typography variant="body1">
                    <strong>Validade:</strong>{" "}
                    {formatarDataGuardada(dados.loteInfo.dataValidade)}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Informações do Descarte
            </Typography>
          </Box>
        )}

        {/* Quantidades ------------------------------------------------- */}
        {temLocalizacoes ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quantidade a Descartar *
            </Typography>

            {varias && (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={modo}
                onChange={(e, valor) => trocarModo(valor)}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="lote">Lote inteiro</ToggleButton>
                <ToggleButton value="localizacao">Por localização</ToggleButton>
              </ToggleButtonGroup>
            )}

            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Localização</TableCell>
                    <TableCell align="right">Disponível</TableCell>
                    <TableCell align="right">Descartar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locs.map((l) => (
                    <TableRow key={l.localizacao._id}>
                      <TableCell>{descreverLocalizacao(l)}</TableCell>
                      <TableCell align="right">{l.quantidade}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          sx={{ width: 110 }}
                          disabled={varias && modo === "lote"}
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: l.quantidade,
                              style: { textAlign: "right" },
                            },
                          }}
                          value={quantidades[l.localizacao._id] ?? ""}
                          onChange={(e) =>
                            setQuantidadeLoc(l.localizacao._id, e.target.value)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {varias && (
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <strong>Total a descartar</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {totalADescartar} {unidade}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Paper>

            {errors.quantidade && (
              <FormHelperText error>{errors.quantidade}</FormHelperText>
            )}
          </Box>
        ) : eliminarLote ? (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "#eceff1",
              borderRadius: 1,
              borderLeft: "4px solid #607d8b",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Este lote está a zero
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Não há quantidade para descartar. O que se pode fazer é eliminar a
              linha do lote, para os casos em que ele foi lançado por engano. Os
              movimentos de entrada e de saída ficam no histórico, e o total do
              produto não muda — um lote esgotado já contava zero.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="Quantidade a Descartar *"
              InputProps={{ inputProps: { min: 1, max: totalDisponivel } }}
              value={quantidadeSimples}
              onChange={(e) =>
                setQuantidadeSimples(
                  limitar(e.target.value, totalDisponivel, quantidadeSimples),
                )
              }
              error={!!errors.quantidade}
              helperText={errors.quantidade}
            />
          </Box>
        )}

        {/* Motivo e observações --------------------------------------- */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.motivo} disabled={eliminarLote}>
              <InputLabel id="motivo-label">Motivo do Descarte *</InputLabel>
              <Select
                labelId="motivo-label"
                name="motivo"
                value={formData.motivo}
                onChange={(e) => setField("motivo", e.target.value)}
                label="Motivo do Descarte *"
              >
                {MOTIVOS_DESCARTE.map((motivo) => (
                  <MenuItem key={motivo.value} value={motivo.value}>
                    {motivo.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.motivo ? (
                <FormHelperText>{errors.motivo}</FormHelperText>
              ) : (
                eliminarLote && (
                  <FormHelperText>
                    Eliminar uma linha só se justifica por engano no lançamento.
                  </FormHelperText>
                )
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={eliminarLote ? "Observações *" : "Observações"}
              value={formData.observacoes}
              onChange={(e) => setField("observacoes", e.target.value)}
              error={!!errors.observacoes}
              helperText={errors.observacoes}
              placeholder={
                eliminarLote
                  ? "Porque é que este lote foi lançado por engano"
                  : "Detalhes adicionais sobre o motivo do descarte"
              }
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "#fff8e1",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {eliminarLote
              ? "Atenção: Esta ação não pode ser desfeita. A linha do lote desaparece do inventário e fica registada no histórico da clínica."
              : "Atenção: Esta ação não pode ser desfeita. O sistema registrará o descarte e atualizará o inventário."}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
        >
          {eliminarLote ? "Eliminar Lote" : "Confirmar Descarte"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDescarteStock;
